import React, { useState, useEffect } from 'react';
import { specialisationName } from '../../constants/staff';
import { useTheme } from '../../theme/ThemeContext';
import apiClient from '../../services/api/apiClient';
import { fetchDoctors as fetchDoctorList } from '../../services/api/visitApi';
import {
  getFreeFollowupSettings,
  updateFreeFollowupSettings,
} from '../../services/api/freeFollowupSettingsApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * FeesSettings — Admin page for configuring consultation fees per doctor
 * and the optional new-patient registration fee.
 *
 * Endpoints:
 *   GET  /api/organisation/settings/fees
 *   PUT  /api/organisation/settings/fees
 *
 * Response shape:
 *   {
 *     doctor_fees: { [doctor_id]: number },
 *     registration_fee_enabled: boolean,
 *     registration_fee: number
 *   }
 * ═══════════════════════════════════════════════════════════════════════
 */

async function fetchFeeSettings() {
  const res = await apiClient.get('/api/organisation/settings/fees');
  return res.data;
}

async function saveFeeSettings(payload) {
  const res = await apiClient.put('/api/organisation/settings/fees', payload);
  return res.data;
}

export default function FeesSettings() {
  const { isDark } = useTheme();

  const [doctors, setDoctors] = useState([]);
  const [doctorFees, setDoctorFees] = useState({});
  const [regFeeEnabled, setRegFeeEnabled] = useState(false);
  const [regFee, setRegFee] = useState('');
  const [followupEnabled, setFollowupEnabled] = useState(false);
  const [followupWindowDays, setFollowupWindowDays] = useState('');
  const [followupScope, setFollowupScope] = useState('cross_doctor');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [{ doctors: docs }, settings, followup] = await Promise.all([
          fetchDoctorList(),
          fetchFeeSettings(),
          getFreeFollowupSettings(),
        ]);
        if (cancelled) return;
        setDoctors(docs || []);
        setDoctorFees(settings.doctor_fees || {});
        setRegFeeEnabled(!!settings.registration_fee_enabled);
        setRegFee(settings.registration_fee != null ? String(settings.registration_fee) : '');
        setFollowupEnabled(!!followup?.enabled);
        setFollowupWindowDays(
          followup?.window_days != null ? String(followup.window_days) : '',
        );
        setFollowupScope(
          followup?.scope === 'same_doctor' ? 'same_doctor' : 'cross_doctor',
        );
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message || 'Failed to load fee settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleDoctorFee = (doctorId, value) => {
    setDoctorFees((prev) => ({ ...prev, [doctorId]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const doctorFeesClean = {};
      for (const [id, val] of Object.entries(doctorFees)) {
        if (val !== '' && val != null) doctorFeesClean[id] = Number(val);
      }
      await saveFeeSettings({
        doctor_fees: doctorFeesClean,
        registration_fee_enabled: regFeeEnabled,
        registration_fee: regFee !== '' ? Number(regFee) : null,
      });
      await updateFreeFollowupSettings({
        enabled: followupEnabled,
        window_days: followupWindowDays !== '' ? Number(followupWindowDays) : 0,
        scope: followupScope,
      });
      setSuccess('Fee settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save fee settings');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    padding: '0.5rem 0.625rem', borderRadius: '0.5rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
    background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
    color: isDark ? '#f5f5f7' : '#111827',
    fontSize: '0.875rem', width: '120px',
  };

  const sectionStyle = {
    borderRadius: '0.75rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    background: isDark ? 'rgba(255,255,255,0.02)' : '#fff',
    padding: '1.25rem',
    marginBottom: '1rem',
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: isDark ? '#9ca3af' : '#6b7280', fontSize: '0.875rem' }}>
        Loading fee settings...
      </div>
    );
  }

  return (
    <div style={{ padding: '1.25rem' }}>
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: isDark ? '#f5f5f7' : '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          💰 Fees & Billing
        </h2>
        <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
          Set consultation fees per doctor. These auto-fill the Billing card at visit registration.
        </p>
      </div>

      {error && (
        <div style={{ padding: '0.625rem 0.875rem', borderRadius: '0.5rem', marginBottom: '1rem', background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2', border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`, color: isDark ? '#fca5a5' : '#991b1b', fontSize: '0.8125rem' }}>
          ⚠️ {error}
        </div>
      )}
      {success && (
        <div style={{ padding: '0.625rem 0.875rem', borderRadius: '0.5rem', marginBottom: '1rem', background: isDark ? 'rgba(34,197,94,0.1)' : '#f0fdf4', border: `1px solid ${isDark ? 'rgba(34,197,94,0.2)' : '#dcfce7'}`, color: isDark ? '#4ade80' : '#166534', fontSize: '0.8125rem' }}>
          ✅ {success}
        </div>
      )}

      {/* Doctor Fees */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '0.9375rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
          Consultation Fee per Doctor
        </h3>
        {doctors.length === 0 && (
          <p style={{ fontSize: '0.8125rem', color: isDark ? '#6b7280' : '#9ca3af' }}>No doctors found.</p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {doctors.map((doc) => (
            <div key={doc.doctor_id || doc.member_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500, color: isDark ? '#f5f5f7' : '#111827' }}>
                  Dr. {doc.name}
                </div>
                {doc.specialization && (
                  <div style={{ fontSize: '0.75rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                    {/* FE-35 — the speciality's NAME, not the stored value. */}
                    {specialisationName(doc.specialization) || doc.specialization}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>₹</span>
                <input
                  type="number"
                  min="0"
                  step="10"
                  placeholder="e.g. 500"
                  value={doctorFees[doc.doctor_id || doc.member_id] ?? ''}
                  onChange={(e) => handleDoctorFee(doc.doctor_id || doc.member_id, e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* New Patient Registration Fee */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
          <div>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '0.9375rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
              New Patient Registration Fee
            </h3>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
              One-time OPD card/file fee charged only on first visit. Shown in Billing card automatically for new patients.
            </p>
          </div>
          {/* Toggle */}
          <button
            onClick={() => setRegFeeEnabled((v) => !v)}
            style={{
              flexShrink: 0, width: '2.75rem', height: '1.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer',
              background: regFeeEnabled ? '#0d9488' : isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb',
              position: 'relative', transition: 'background 0.2s',
            }}
          >
            <span style={{
              position: 'absolute', top: '2px',
              left: regFeeEnabled ? 'calc(100% - 1.25rem - 2px)' : '2px',
              width: '1.25rem', height: '1.25rem', borderRadius: '50%',
              background: '#fff', transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>

        {regFeeEnabled && (
          <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>₹</span>
            <input
              type="number"
              min="0"
              step="5"
              placeholder="e.g. 100"
              value={regFee}
              onChange={(e) => setRegFee(e.target.value)}
              style={inputStyle}
            />
            <span style={{ fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280' }}>per new patient</span>
          </div>
        )}
      </div>

      {/* Free Follow-up Window */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
          <div>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '0.9375rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
              Free Follow-up Window
            </h3>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
              Waive the consultation fee when a patient returns within this many days of
              their last consultation. The day count is inclusive — a 7-day window keeps
              day 7 free and charges from day 8.
            </p>
          </div>
          {/* Toggle */}
          <button
            onClick={() => setFollowupEnabled((v) => !v)}
            style={{
              flexShrink: 0, width: '2.75rem', height: '1.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer',
              background: followupEnabled ? '#0d9488' : isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb',
              position: 'relative', transition: 'background 0.2s',
            }}
          >
            <span style={{
              position: 'absolute', top: '2px',
              left: followupEnabled ? 'calc(100% - 1.25rem - 2px)' : '2px',
              width: '1.25rem', height: '1.25rem', borderRadius: '50%',
              background: '#fff', transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>

        {followupEnabled && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="number"
                min="0"
                max="365"
                step="1"
                placeholder="e.g. 7"
                value={followupWindowDays}
                onChange={(e) => setFollowupWindowDays(e.target.value)}
                style={inputStyle}
              />
              <span style={{ fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                days (0 = same day only)
              </span>
            </div>

            {/* Doctor scope — who the free follow-up is valid for */}
            <div style={{ marginTop: '1rem' }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem', color: isDark ? '#f5f5f7' : '#111827' }}>
                Valid for
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {[
                  { value: 'cross_doctor', label: 'Any doctor' },
                  { value: 'same_doctor', label: 'Same doctor only' },
                ].map((opt) => {
                  const active = followupScope === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFollowupScope(opt.value)}
                      style={{
                        padding: '0.4rem 0.75rem', borderRadius: '0.5rem', cursor: 'pointer',
                        fontSize: '0.8125rem', fontWeight: 600, transition: 'all 0.15s',
                        border: `1px solid ${active ? '#0d9488' : (isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)')}`,
                        background: active ? '#0d9488' : (isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb'),
                        color: active ? '#fff' : (isDark ? '#f5f5f7' : '#111827'),
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                “Same doctor only” charges the consultation fee when a patient returns within the
                window to a <em>different</em> doctor (a fresh consultation).
              </p>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          padding: '0.75rem 1.5rem', borderRadius: '0.75rem', border: 'none',
          background: 'linear-gradient(135deg, #0d9488, #14b8a6)', color: '#fff',
          fontWeight: 600, fontSize: '0.9375rem', cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.7 : 1, boxShadow: '0 4px 12px rgba(13,148,136,0.3)',
        }}
      >
        {saving ? 'Saving...' : '💾 Save Fee Settings'}
      </button>
    </div>
  );
}
